import { useState } from 'react';
import { Search, LogOut, Sun, Moon, Check, PanelLeft, Shield, ShieldCheck } from 'lucide-react';
import { CommandPalette } from '@/components/CommandPalette';
import { FeedbackButton } from '@/components/feedback/FeedbackButton';
import { TabNav } from './TabNav';
import { Breadcrumb } from './Breadcrumb';
import { useNavigation } from '@/contexts/NavigationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/hooks/useTheme';
import { useProfile } from '@/hooks/useProfile';
import { useIsMobile } from '@/hooks/use-mobile';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { UserDataSettings } from '@/components/UserDataSettings';

const THEMES = [
  { id: 'light', label: 'Caldeira Light', icon: Sun },
  { id: 'dark', label: 'Caldeira Dark', icon: Moon },
] as const;

export const Topbar = () => {
  const { navigation, sidebarCollapsed, setSidebarCollapsed, navigateToAdmin } = useNavigation();
  const { user, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const { isAdmin } = useProfile();
  const isMobile = useIsMobile();
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const displayName = user?.user_metadata?.full_name || user?.email || 'Usuário';
  const avatarUrl = user?.user_metadata?.avatar_url;
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <header className={`h-[60px] bg-topbar border-b border-color flex items-center px-4 gap-3 transition-all duration-300 ${!isMobile && !sidebarCollapsed ? 'ml-64' : 'ml-0'}`}>
      {/* Logo / toggle — visible when sidebar is collapsed */}
      <div
        className={`transition-all duration-300 overflow-hidden flex-shrink-0 ${
          sidebarCollapsed || isMobile ? 'opacity-100 scale-100' : 'w-0 opacity-0 scale-75'
        }`}
      >
        <button
          onClick={() => setSidebarCollapsed(false)}
          className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-md hover:bg-hover transition-colors"
          title="Abrir sidebar"
        >
          <PanelLeft className="h-4 w-4 text-secondary" />
        </button>
      </div>

      {/* Environment context (Breadcrumb content) */}
      <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
        <Breadcrumb />
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Right side: feedback + search + TabNav + Avatar */}
      <div className="flex items-center gap-3 flex-shrink-0">
        <FeedbackButton />
        {/* Search trigger */}
        <button
          onClick={() => setSearchOpen(true)}
          className="hidden md:flex items-center gap-2 w-44 h-8 px-3 rounded-md border border-color bg-secondary/50 text-xs text-tertiary hover:bg-hover transition-colors"
        >
          <Search className="w-3.5 h-3.5 shrink-0" />
          <span className="flex-1 text-left">Buscar...</span>
          <kbd className="pointer-events-none hidden sm:inline-flex h-5 select-none items-center gap-0.5 rounded border border-color bg-surface px-1.5 font-mono text-[10px] font-medium text-tertiary">
            ⌘K
          </kbd>
        </button>
        <button
          onClick={() => setSearchOpen(true)}
          className="md:hidden flex items-center justify-center w-8 h-8 rounded-md hover:bg-hover transition-colors"
        >
          <Search className="w-4 h-4 text-secondary" />
        </button>
        <CommandPalette open={searchOpen} onOpenChange={setSearchOpen} />

        <div className="hidden md:block">
          <TabNav />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="rounded-full focus:outline-none focus:ring-2 focus:ring-brand-primary">
              <Avatar className="h-8 w-8">
                {avatarUrl && <AvatarImage src={avatarUrl} alt={displayName} />}
                <AvatarFallback className="text-xs font-medium bg-secondary text-primary">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <div className="px-2 py-1.5 text-sm font-medium text-primary truncate">
              {displayName}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
              Tema
            </DropdownMenuLabel>
            {THEMES.map((t) => (
              <DropdownMenuItem
                key={t.id}
                onClick={() => setTheme(t.id)}
                className="cursor-pointer flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <t.icon className="h-4 w-4" />
                  {t.label}
                </span>
                {theme === t.id && <Check className="h-4 w-4 text-brand-primary" />}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setPrivacyOpen(true)} className="cursor-pointer">
              <ShieldCheck className="mr-2 h-4 w-4" />
              Meus Dados e Privacidade
            </DropdownMenuItem>
            {isAdmin && (
              <>
                <DropdownMenuItem onClick={navigateToAdmin} className="cursor-pointer">
                  <Shield className="mr-2 h-4 w-4" />
                  Administração
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={signOut} className="text-destructive cursor-pointer">
              <LogOut className="mr-2 h-4 w-4" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
          <DialogContent className="sm:max-w-[520px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                Meus Dados e Privacidade
              </DialogTitle>
            </DialogHeader>
            <UserDataSettings />
          </DialogContent>
        </Dialog>
      </div>
    </header>
  );
};
